import React, { useEffect, useState } from 'react';
import { api } from '../../../helpers/api';
import {
  useAllGamesAdmin,
  useDeleteGame,
  useSecurePing,
  useUpdateGame,
} from '../../../helpers/useApi';
import { GameState, GameWithDate } from '../../../types/types';
import { PuzzleBuilder } from './PuzzleBuilder/PuzzleBuilder';
import Game from '../../game/Game';
import './AdminPortal.css';
import { Button } from '@mui/material';

function AdminPortal() {
  const securePingState = useSecurePing(true);
  const allGames = useAllGamesAdmin();
  const deleteGameMutation = useDeleteGame();
  const updateGameMutation = useUpdateGame();
  const [gameState, setGameState] = useState<GameState>();

  useEffect(() => {
    const token = api.getToken();
    if (!token) {
      console.error('No token found, redirecting to login');
      window.location.href = '/';
    }
  }, []);

  const handleDelete = async (game: GameWithDate) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the game on ${game.date} - ${game.game.theme}?`,
      )
    )
      return;

    deleteGameMutation.mutate(
      { id: game._id },
      {
        onSuccess: () => {
          allGames.refetch(); // reload the list
        },
        onError: (error) => {
          alert(`Failed to delete: ${(error as Error).message}`);
        },
      },
    );
  };

  const handleUpdate = (game: GameWithDate) => {
    if (
      !window.confirm(
        `Are you sure you want to update the game on ${game.date} - ${game.game.theme}?`,
      )
    )
      return;
    updateGameMutation.mutate(
      { game },
      {
        onSuccess: () => {
          allGames.refetch();
        },
        onError: (error) => {
          alert(`Failed to update: ${(error as Error).message}`);
        },
      },
    );
  };

  return (
    <div className="admin-portal">
      <header className="admin-header">
        <h1>Admin Portal</h1>
        {securePingState.error ? (
          <p style={{ color: 'red' }}>
            {securePingState.error instanceof Error
              ? securePingState.error.message
              : String(securePingState.error)}
          </p>
        ) : (
          <p>{securePingState.data?.message}</p>
        )}
      </header>
      <section className="section">
        <div className="builder-section">
          <div className="builder-col">
            <PuzzleBuilder setGameState={setGameState} gameState={gameState} />
          </div>

          {gameState && (
            <div className="builder-col">
              <Game
                gameState={gameState}
                setGameState={setGameState}
                resultsOnClick={() => {
                  throw new Error('Function not implemented.');
                }}
              />
            </div>
          )}
        </div>
      </section>
      <section className="section">
        <h2>All Games</h2>
        {allGames.error ? (
          <p style={{ color: 'red' }}>
            {allGames.error instanceof Error
              ? allGames.error.message
              : String(allGames.error)}
          </p>
        ) : allGames.data ? (
          allGames.data.length > 0 ? (
            <div className="table-wrapper">
              <table className="game-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>ID</th>
                    <th>Theme</th>
                    <th>Words</th>
                  </tr>
                </thead>
                <tbody>
                  {allGames.data.map((game) => (
                    <GameRow
                      key={game.date}
                      game={game}
                      onDelete={handleDelete}
                      onUpdate={handleUpdate}
                      isDeleting={
                        deleteGameMutation.isPending &&
                        deleteGameMutation.variables?.id === game._id
                      }
                      isUpdating={
                        updateGameMutation.isPending &&
                        updateGameMutation.variables?.game._id === game._id
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No games found.</p>
          )
        ) : (
          <p>Loading games...</p>
        )}
      </section>
    </div>
  );
}

function GameRow({
  game,
  onDelete,
  onUpdate,
  isDeleting,
  isUpdating,
}: {
  game: GameWithDate;
  onDelete: (game: GameWithDate) => void;
  onUpdate: (game: GameWithDate) => void;
  isDeleting: boolean;
  isUpdating: boolean;
}) {
  const [editing, setEditing] = React.useState(false);
  const [editDate, setEditDate] = React.useState(game.date);
  const [editTheme, setEditTheme] = React.useState(game.game.theme);

  const save = () => {
    onUpdate({
      ...game,
      date: editDate,
      game: { ...game.game, theme: editTheme },
    });
    setEditing(false);
  };

  const cancel = () => {
    setEditDate(game.date);
    setEditTheme(game.game.theme);
    setEditing(false);
  };

  return (
    <tr>
      <td>
        {editing ? (
          <input
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            style={{ width: '120px' }}
          />
        ) : (
          game.date
        )}
      </td>

      <td>{game._id}</td>

      <td>
        {editing ? (
          <input
            value={editTheme}
            onChange={(e) => setEditTheme(e.target.value)}
            style={{ width: '120px' }}
          />
        ) : (
          game.game.theme
        )}
      </td>

      <td>
        {game.game.words.map((word, i) => (
          <span key={i} className="word-pill">
            {word.text}
          </span>
        ))}
      </td>

      <td style={{ whiteSpace: 'nowrap' }}>
        {editing ? (
          <>
            <Button
              variant="outlined"
              onClick={save}
              disabled={isUpdating}
              style={{ marginRight: 6 }}
            >
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={cancel}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={() => setEditing(true)}
              style={{ marginRight: 6 }}
            >
              Edit
            </Button>
            <Button variant="outlined" onClick={() => onDelete(game)}>
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        )}
      </td>
    </tr>
  );
}

export default AdminPortal;
